'use client';

import React, { useEffect, useRef } from 'react';
import { Monitor, Cpu, Zap, Database } from 'lucide-react';

const services = [
  {
    icon: Database,
    title: 'Data Science',
    subtitle: 'Analitik & Wawasan',
    description: 'Transformasi data mentah menjadi wawasan bisnis — analitik prediktif, machine learning, big data, dan visualisasi data interaktif.',
    highlights: ['Predictive AI', 'Neural Networks', 'Big Data Mining', 'Real-time Dashboards'],
  },
  {
    icon: Monitor,
    title: 'Website Development',
    subtitle: 'AgencyHUB by STUDO.ID',
    description: 'Lini kreatif kami yang fokus membangun website profesional untuk bisnis — landing page, web app, e-commerce, hingga company profile.',
    highlights: ['High-Conversion LP', 'Immersive WebGL', 'Headless E-Com', 'Progressive Web Apps'],
  },
  {
    icon: Cpu,
    title: 'AI Integration',
    subtitle: 'Kecerdasan Buatan',
    description: 'Integrasi AI ke dalam produk dan sistem bisnis — chatbot cerdas, analitik prediktif, content generation, dan personalisasi otomatis.',
    highlights: ['Autonomous Agents', 'RAG & LLM', 'Generative UI', 'Vision AI'],
  },
  {
    icon: Zap,
    title: 'Automation',
    subtitle: 'Otomasi Proses',
    description: 'Otomasi alur kerja digital — dari broadcast WhatsApp, CRM sync, hingga pipeline data real-time yang menghemat waktu dan biaya operasional.',
    highlights: ['Zero-Touch Ops', 'Omnichannel Bots', 'Smart Workflows', 'Real-time Sync'],
  },
];

export default function LayananSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ctx: { revert: () => void };
    const init = async () => {
      const { gsap } = await import('gsap');
      const { ScrollTrigger } = await import('gsap/ScrollTrigger');
      gsap.registerPlugin(ScrollTrigger);
      ctx = gsap.context(() => {
        gsap.fromTo(titleRef.current, { opacity: 0, y: 32, filter: 'blur(8px)' },
          { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.8, ease: 'power2.out', scrollTrigger: { trigger: sectionRef.current, start: 'top 75%' } });
        const cards = cardsRef.current?.querySelectorAll('.svc-card');
        if (cards) gsap.fromTo(cards, { opacity: 0, y: 48, filter: 'blur(8px)' },
          { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.8, stagger: 0.15, ease: 'power2.out', scrollTrigger: { trigger: cardsRef.current, start: 'top 80%' } });
      }, sectionRef);
    };
    init();
    return () => { if (ctx) ctx.revert(); };
  }, []);

  return (
    <section id="layanan" ref={sectionRef} className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div ref={titleRef} className="opacity-0 mb-16 md:mb-20">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-lime-500" />
            <span className="text-lime-500 text-[11px] font-semibold tracking-[0.2em] uppercase">Yang Kami Ciptakan</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-light text-white tracking-tight leading-[1.1] max-w-xl">
            Lini{' '}
            <span className="font-semibold" style={{ background: 'linear-gradient(135deg, #a3e635, #84cc16)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Kreatif</span>{' '}Kami
          </h2>
          <p className="mt-4 text-zinc-400 text-base md:text-lg max-w-lg leading-relaxed">
            Empat pilar utama yang menjadi fondasi kreatifitas STUDO.ID.
          </p>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {services.map(svc => (
            <div key={svc.title} className="svc-card spotlight-card opacity-0 group rounded-2xl p-[1px]"
              style={{ background: 'radial-gradient(circle, rgb(24,24,27) 0%, rgb(0,0,0) 100%)' }}>
              <div className="rounded-[15px] p-7 md:p-8 flex flex-col gap-6 h-full min-h-[380px] bg-[rgba(15,15,18,0.9)]">
                <div className="w-12 h-12 rounded-md flex items-center justify-center border border-white/10 bg-white/[0.04] group-hover:border-lime-500/40 transition-colors">
                  <svc.icon size={22} className="text-lime-500" />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-zinc-500">{svc.subtitle}</span>
                  <h3 className="text-xl font-semibold text-white tracking-tight">{svc.title}</h3>
                </div>
                <p className="text-zinc-400 text-[14px] leading-relaxed flex-1">{svc.description}</p>
                <div className="flex flex-wrap gap-2 mt-auto">
                  {svc.highlights.map(tag => (
                    <span key={tag} className="px-3 py-1 rounded-full text-[11px] font-medium text-zinc-400 border border-white/[0.08] bg-white/[0.03] hover:border-lime-500/40 hover:text-lime-400 transition-all">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
